/** biome-ignore-all lint/style/noExcessiveLinesPerFile: unified widget */
import { yupResolver } from "@hookform/resolvers/yup";
import { parseIncompletePhoneNumber } from "libphonenumber-js";
import { Check, ChevronDown, ChevronUp, Zap } from "lucide-react";
import {
  type ChangeEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useController, useForm, useWatch } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";

import { requestedLoanAmountValues } from "@krea/shared/constants";
import { Badge } from "@krea/shared/design-system/Badge";
import { Button } from "@krea/shared/design-system/Button";
import {
  CombinedInputSlider,
  type ICombinedInputSliderProps,
} from "@krea/shared/design-system/CombinedInputSlider";
import CheckBox from "@krea/shared/design-system/fields/Checkbox";
import { EmailInput } from "@krea/shared/design-system/fields/EmailInput";
import { OrganisationInput } from "@krea/shared/design-system/fields/OrganisationInput";
import { PhoneInput } from "@krea/shared/design-system/fields/PhoneInput";
import { useFormatCurrency } from "@krea/shared/hooks/useFormatCurrency";
import {
  CountryCode,
  type IPreliminaryLoanAmountData,
  type SupportedLanguage,
} from "@krea/shared/types/common";
import {
  amountValuesList,
  cn,
  getCompanyBrandName,
  isLimitedCompany,
} from "@krea/shared/utils";

import { LoadingSpinner } from "./components/LoadingSpinner";
import {
  LoanWidgetResult,
  loanWidgetResultTitleClassName,
} from "./LoanWidgetResult";
import {
  getAdtractionRedirectUrl,
  getDefaultRequestedLoanAmount,
  getUtmData,
  getValidationSchema,
  type ICustomExceptionBody,
  type IPersistedWidgetState,
  LOAN_WIDGET_SCROLL_TARGET_ATTR,
  loadPersistedWidgetState,
  PrivacyPolicyAnchor,
  recenterInViewport,
  sessionStorageSafe,
  type ValidationSchemaType,
} from "./utils";

// ── Props ─────────────────────────────────────────────────────────────────────

export type LoanWidgetProps = {
  countryCode: CountryCode;
  currency: string;
  locale: string;
  backendBaseUrl: string;
  customerWebUrl: string;
  countryWebsiteUrl: string;
  utmSourceIdentifier?: string;
  utmCampaign?: string;
  /** Override the form heading. */
  formTitle?: string;
  /** Override the submit button CTA text. */
  ctaText?: string;
  /** Override the benefit badge text shown beneath the form heading. */
  benefitText?: string;
  renderRequestedAmountField?: (args: ICombinedInputSliderProps) => ReactNode;
  onOrganisationChange?: (organisation: {
    name: string;
    organisationNumber: string;
  }) => void;
  onEmailChange?: (email: string) => void;
  onMobilePhoneNumberChange?: (phone: string) => void;
  onRequestedLoanAmountChange?: (amount: number) => void;
  onConsentChange?: (consent: boolean) => void;
  onClearFormError?: (field: string) => void;
  defaultOrganisation?: { name: string; organisationNumber: string };
  defaultEmail?: string;
  defaultMobilePhoneNumber?: string;
  defaultRequestedLoanAmount?: number;
  defaultConsentCheckbox?: boolean;
  collapsible?: boolean;
  persistKey?: string;
  showUsps?: boolean;
  redirectStep?: string;
  affiliatePartnerId?: string;
  environment?: string;
  initialPreliminaryBid?: IPreliminaryLoanAmountData;
  renderReviewBadges?: (countryCode: CountryCode) => ReactNode;
  onClearPreliminaryBid?: () => void;
  onSubmitSuccess?: (data: IPreliminaryLoanAmountData) => void;
  /** Override the result-view heading. */
  resultTitle?: string;
  /** Portal container for tooltips/drawers in the result view. Pass the widget root to keep portaled content inside scoped styles. */
  portalContainer?: HTMLElement | null;
  /** When provided, renders a "contact us" link in the improve-chances accordion. Omit for third-party widget embeds. */
  onContactUs?: () => void;
  /** How the advisory (edge case) CTA opens the booking calendar. Defaults to `"newTab"` for third-party widget embeds; pass `"modal"` on our own websites and apps. */
  advisoryBooking?: "modal" | "newTab";
  /**
   * Scroll the widget back into view on each user-triggered step change
   * (submit → loading → result, and back to the form). Opt-in: a third-party
   * embed should not scroll its host page. The scroll target is the closest
   * ancestor carrying LOAN_WIDGET_SCROLL_TARGET_ATTR (else the widget itself);
   * its scroll-margin-top is the sticky-header offset. KREA-4760.
   */
  recenterOnStepChange?: boolean;
};

// ── Component ─────────────────────────────────────────────────────────────────

export const LoanWidget = (props: LoanWidgetProps) => {
  const {
    countryCode,
    currency,
    locale,
    backendBaseUrl,
    customerWebUrl,
    countryWebsiteUrl,
    utmSourceIdentifier,
    utmCampaign,
    formTitle,
    ctaText,
    benefitText,
    renderRequestedAmountField,
    onOrganisationChange,
    onEmailChange,
    onMobilePhoneNumberChange,
    onRequestedLoanAmountChange,
    onConsentChange,
    onClearFormError,
    defaultOrganisation = { name: "", organisationNumber: "" },
    defaultEmail = "",
    defaultMobilePhoneNumber = "",
    defaultRequestedLoanAmount,
    defaultConsentCheckbox = false,
    collapsible = false,
    persistKey,
    showUsps = true,
    affiliatePartnerId,
    environment,
    redirectStep,
    initialPreliminaryBid,
    renderReviewBadges,
    onClearPreliminaryBid,
    resultTitle,
    portalContainer,
    onContactUs,
    advisoryBooking,
    recenterOnStepChange = false,
  } = props;

  const { t, i18n } = useTranslation("loanWidget");
  const initialRequestedLoanAmount =
    defaultRequestedLoanAmount ?? getDefaultRequestedLoanAmount(countryCode);
  const [isSubmitting, setSubmitting] = useState(false);
  const [, currencySymbol] = useFormatCurrency(locale, { currency });

  const [storedState] = useState<IPersistedWidgetState | null>(() =>
    persistKey ? loadPersistedWidgetState(persistKey) : null,
  );

  // The persisted session is deliberately NOT read into the first render: this
  // component is server-rendered (removing `ssr: false` is what fixes the hero's
  // LCP), and sessionStorage does not exist there. Reading it here would make the
  // client's first render disagree with the server HTML for any returning user.
  // It is applied just below, in an effect, after hydration.
  const [preliminaryBid, setPreliminaryBid] = useState<
    IPreliminaryLoanAmountData | undefined
  >(initialPreliminaryBid);
  const [showResult, setShowResult] = useState(!!initialPreliminaryBid);
  const [hydrated, setHydrated] = useState(false);
  const [formExpanded, setFormExpanded] = useState(false);
  const expandToggleRef = useRef<HTMLButtonElement>(null);
  const prevShowResultRef = useRef(showResult);

  useEffect(() => {
    if (
      collapsible &&
      prevShowResultRef.current === true &&
      showResult === false
    ) {
      setFormExpanded(true);
    }
    prevShowResultRef.current = showResult;
  }, [collapsible, showResult]);

  // ── Recentre on step change (KREA-4760) ──────────────────────────────────────
  const step =
    showResult && preliminaryBid ? "result" : isSubmitting ? "loading" : "form";
  // Rendered first in every step, so the scroll target can be found from it.
  const stepAnchorRef = useRef<HTMLSpanElement>(null);
  // Only steps the user caused: restoring a persisted result after hydration
  // also changes the step, and must not scroll the page on load.
  const userChangedStepRef = useRef(false);
  // Pending 3s loading-floor timer; cleared on go back so a stale callback can't
  // end the next submission's loading state early.
  const loadingFloorTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const prevStepRef = useRef(step);

  useEffect(() => {
    if (prevStepRef.current === step) return;
    prevStepRef.current = step;
    if (!(recenterOnStepChange && userChangedStepRef.current)) return;
    const anchor = stepAnchorRef.current;
    const target =
      anchor?.closest<HTMLElement>(`[${LOAN_WIDGET_SCROLL_TARGET_ATTR}]`) ??
      anchor?.parentElement;
    if (!target) return;
    // Two frames: hosts that animate their height to the step's size (the hero
    // banner) apply it in a ResizeObserver, so measure once that has run.
    let innerFrame = 0;
    const outerFrame = requestAnimationFrame(() => {
      innerFrame = requestAnimationFrame(() => recenterInViewport(target));
    });
    return () => {
      cancelAnimationFrame(outerFrame);
      cancelAnimationFrame(innerFrame);
    };
  }, [step, recenterOnStepChange]);

  const sliderValues = useMemo(() => {
    switch (countryCode) {
      case CountryCode.SE:
        return amountValuesList(requestedLoanAmountValues, 1);
      case CountryCode.FI:
        return amountValuesList(requestedLoanAmountValues, 10);
      default:
        return amountValuesList(requestedLoanAmountValues, 1);
    }
  }, [countryCode]);

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    setError,
    formState,
    reset,
  } = useForm<ValidationSchemaType>({
    mode: "onChange",
    defaultValues: {
      organisation: {
        name: defaultOrganisation.name,
        organisationNumber: defaultOrganisation.organisationNumber,
      },
      email: defaultEmail,
      mobilePhoneNumber: defaultMobilePhoneNumber,
      requestedLoanAmount: initialRequestedLoanAmount,
      consentCheckbox: defaultConsentCheckbox,
    },
    resolver: yupResolver(getValidationSchema(countryCode)),
  });

  // Restore the persisted session once, after hydration. Declared BEFORE the
  // persist effect below so it wins the first commit — otherwise that effect
  // would write the empty defaults over the very state being restored.
  useEffect(() => {
    if (hydrated) {
      return;
    }
    setHydrated(true);
    if (!storedState) {
      return;
    }
    reset({
      organisation: {
        name: storedState.organisation?.name ?? defaultOrganisation.name,
        organisationNumber:
          storedState.organisation?.organisationNumber ??
          defaultOrganisation.organisationNumber,
      },
      email: storedState.email ?? defaultEmail,
      mobilePhoneNumber:
        storedState.mobilePhoneNumber ?? defaultMobilePhoneNumber,
      requestedLoanAmount:
        storedState.requestedLoanAmount ?? initialRequestedLoanAmount,
      consentCheckbox: storedState.consentCheckbox ?? defaultConsentCheckbox,
    });
    if (storedState.preliminaryBid) {
      setPreliminaryBid(storedState.preliminaryBid);
    }
    if (storedState.showResult) {
      setShowResult(true);
    }
  }, [
    hydrated,
    storedState,
    reset,
    defaultOrganisation.name,
    defaultOrganisation.organisationNumber,
    defaultEmail,
    defaultMobilePhoneNumber,
    initialRequestedLoanAmount,
    defaultConsentCheckbox,
  ]);

  const watchedOrganisationName =
    useWatch({ control, name: "organisation.name" }) ?? "";

  const watchedAllFields = useWatch({ control });
  useEffect(() => {
    if (!persistKey || !hydrated) {
      return;
    }
    const stateToStore = {
      organisation: watchedAllFields.organisation,
      email: watchedAllFields.email,
      mobilePhoneNumber: watchedAllFields.mobilePhoneNumber,
      requestedLoanAmount: watchedAllFields.requestedLoanAmount,
      consentCheckbox: watchedAllFields.consentCheckbox ?? false,
      showResult,
      ...(showResult && preliminaryBid ? { preliminaryBid } : {}),
    } satisfies IPersistedWidgetState;
    sessionStorageSafe.set(persistKey, JSON.stringify(stateToStore));
  }, [persistKey, hydrated, watchedAllFields, showResult, preliminaryBid]);

  const onSubmit = async () => {
    userChangedStepRef.current = true;
    setSubmitting(true);
    const startTime = Date.now();
    const formValues = getValues();
    const {
      organisation: { name: organisationName, organisationNumber },
      email,
      mobilePhoneNumber,
      requestedLoanAmount,
    } = formValues;

    const utmData = getUtmData(
      utmSourceIdentifier ?? null,
      utmCampaign ?? null,
    );

    const payload: Record<string, string | number> = {
      countryCode,
      email,
      mobilePhoneNumber,
      requestedLoanAmount,
      organisationName,
      organisationNumber,
      utmData: JSON.stringify(utmData),
    };

    try {
      const response = await fetch(
        `${backendBaseUrl}/api/v1/preliminary-loan-amount`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            // biome-ignore lint/style/useNamingConvention: HTTP header name
            Accept: "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      const finishTime = Date.now();
      const timeDiff = finishTime - startTime;

      if (!response.ok) {
        let errorBody: ICustomExceptionBody | undefined;
        try {
          errorBody = (await response.json()) as ICustomExceptionBody;
        } catch {
          // ignore parse errors
        }
        if (errorBody?.errors?.find((e) => e.code === "1006")) {
          setError("email", {
            message: t("applicationForm.commons.emailErrors.validationError"),
          });
        }
        setSubmitting(false);
        return;
      }

      const delayedFinish = () => {
        if (timeDiff >= 3000) {
          setSubmitting(false);
        } else {
          loadingFloorTimerRef.current = setTimeout(
            () => setSubmitting(false),
            3000 - timeDiff,
          );
        }
      };

      onOrganisationChange?.({ name: organisationName, organisationNumber });
      onEmailChange?.(email);
      onMobilePhoneNumberChange?.(
        parseIncompletePhoneNumber(mobilePhoneNumber) ?? mobilePhoneNumber,
      );

      const data = (await response.json()) as IPreliminaryLoanAmountData;
      delayedFinish();
      onRequestedLoanAmountChange?.(
        data.useEdgeCaseLogic
          ? requestedLoanAmount
          : data.estimatedMaxLoanAmount,
      );
      setPreliminaryBid(data);
      setShowResult(true);
      props.onSubmitSuccess?.(data);
    } catch {
      setSubmitting(false);
    }
  };

  const {
    field: organisationNumberField,
    fieldState: organisationNumberFieldState,
  } = useController({ name: "organisation.organisationNumber", control });

  const { field: emailField, fieldState: emailFieldState } = useController({
    name: "email",
    control,
    defaultValue: "",
  });

  const { field: phoneNumberField, fieldState: phoneNumberFieldState } =
    useController({ name: "mobilePhoneNumber", control });

  const { field: consentCheckboxField, fieldState: consentCheckboxFieldState } =
    useController({ name: "consentCheckbox", control, defaultValue: false });

  const {
    field: { value, onChange: onInputChange, onBlur: onInputBlur },
    fieldState,
  } = useController({ name: "requestedLoanAmount", control });

  const onAmountChange = (newValue: number) => {
    onInputChange(newValue);
    onRequestedLoanAmountChange?.(newValue);
  };

  const isLimitedCompanyResult =
    !!watchedOrganisationName &&
    !organisationNumberFieldState.error &&
    organisationNumberFieldState.isTouched &&
    isLimitedCompany(watchedOrganisationName, countryCode);

  // ── Computed text values ───────────────────────────────────────────────────

  const formTitleText =
    formTitle ?? t("loanWidget.preliminaryLoanAmountHeading");

  const resultTitleText =
    resultTitle ?? t("loanWidget.yourEstimatedCreditLimit");

  let defaultSubmitButtonText = "";
  let defaultBenefitText = "";
  switch (countryCode) {
    case CountryCode.SE:
      defaultSubmitButtonText = t("loanWidget.estimateLimitWithoutCreditCheck");
      defaultBenefitText = t("loanWidget.immediateResponseWithoutCreditCheck");
      break;
    case CountryCode.FI:
      defaultSubmitButtonText = t("loanWidget.estimateLimit");
      defaultBenefitText = t("loanWidget.immediateResponse");
      break;
    default: {
      const exhaustiveCheck: never = countryCode;
      console.error(`Exhaustive check failed: ${exhaustiveCheck}`);
    }
  }

  const submitButtonText = ctaText ?? defaultSubmitButtonText;
  const benefitTextToUse = benefitText ?? defaultBenefitText;

  // ── Continue-to-apply ──────────────────────────────────────────────────────

  const onContinueToApply = () => {
    const formValues = getValues();
    const utmData = getUtmData(
      utmSourceIdentifier ?? null,
      utmCampaign ?? null,
    );
    const params: Record<string, string> = {
      amount: formValues.requestedLoanAmount.toString(),
      orgNumber: formValues.organisation.organisationNumber,
      orgName: formValues.organisation.name,
      email: formValues.email,
      phone: formValues.mobilePhoneNumber,
      consent: formValues.consentCheckbox ? "true" : "false",
      ...(redirectStep ? { step: redirectStep } : {}),
      ...utmData,
    };

    let url = `${customerWebUrl}/application?${new URLSearchParams(params).toString()}`;

    if (
      utmSourceIdentifier === "adtraction" &&
      affiliatePartnerId &&
      environment
    ) {
      const adtractionUrl = getAdtractionRedirectUrl({
        countryCode,
        environment,
        ourLink: url,
        affiliatePartnerId,
      });
      if (adtractionUrl) {
        url = adtractionUrl;
      }
    }

    window.open(url, "_blank");
  };

  // ── Result view ────────────────────────────────────────────────────────────

  if (showResult && preliminaryBid) {
    return (
      <>
        <span ref={stepAnchorRef} aria-hidden="true" className="block" />
        <h3 className={loanWidgetResultTitleClassName}>{resultTitleText}</h3>
        <LoanWidgetResult
          countryCode={countryCode}
          currency={currency}
          locale={locale}
          organisationName={watchedOrganisationName}
          email={watchedAllFields.email}
          mobilePhoneNumber={watchedAllFields.mobilePhoneNumber}
          preliminaryBid={preliminaryBid}
          requestedLoanAmount={getValues("requestedLoanAmount")}
          onGoBack={() => {
            userChangedStepRef.current = true;
            // A fast response leaves the 3s loading floor running; without this the
            // widget would flash result -> loading -> form and recentre twice.
            clearTimeout(loadingFloorTimerRef.current);
            setSubmitting(false);
            setShowResult(false);
            setPreliminaryBid(undefined);
            onClearPreliminaryBid?.();
          }}
          onContinueToApply={onContinueToApply}
          utmSourceIdentifier={utmSourceIdentifier}
          environment={environment}
          renderReviewBadges={renderReviewBadges}
          showUsps={showUsps}
          portalContainer={portalContainer}
          onContactUs={onContactUs}
          advisoryBooking={advisoryBooking}
        />
      </>
    );
  }

  // ── Form view ──────────────────────────────────────────────────────────────

  const requestedAmountSliderProps: ICombinedInputSliderProps = {
    name: "requestedLoanAmount",
    value,
    valuesList: sliderValues,
    onChange: onAmountChange,
    label: t("applicationForm.commons.requestedAmountPreLabel"),
    textAppend: value !== 0 ? (currencySymbol ?? "") : "",
    placeholder: t("applicationForm.commons.exactRequestedAmountPlaceholder"),
    size: "lg",
    error:
      fieldState.isTouched && fieldState.error?.message
        ? fieldState.error.message
        : undefined,
    onBlur: onInputBlur,
    portalContainer,
  };

  const organisationInput = (
    <OrganisationInput
      value={organisationNumberField.value}
      countryCode={countryCode}
      backendBaseUrl={backendBaseUrl ?? ""}
      error={
        formState.errors.organisation?.organisationNumber?.message ??
        formState.errors.organisation?.name?.message
      }
      touched={organisationNumberFieldState.isTouched}
      onCompanyChange={(company) => {
        setValue(
          "organisation.organisationNumber",
          company.organisationNumber,
          {
            shouldTouch: true,
            shouldDirty: true,
            shouldValidate: true,
          },
        );
        setValue("organisation.name", company.name, {
          shouldTouch: true,
          shouldDirty: true,
          shouldValidate: true,
        });
        onOrganisationChange?.(company);
      }}
      onBlur={organisationNumberField.onBlur}
      isLimitedCompany={isLimitedCompanyResult}
    />
  );

  const restFields = (
    <>
      <div className="mt-7">
        {renderRequestedAmountField ? (
          renderRequestedAmountField(requestedAmountSliderProps)
        ) : (
          <CombinedInputSlider {...requestedAmountSliderProps} />
        )}
      </div>
      {/* Email and phone share a row when both get >= 232px (hero banner,
          wide embeds) and wrap to a stack otherwise; gap-y-5 keeps the
          stacked spacing the two had before. */}
      <div className="mt-5 mb-4 flex flex-wrap gap-x-4 gap-y-5">
        <div className="min-w-0 grow basis-58">
          <EmailInput
            value={emailField.value}
            countryCode={countryCode}
            touched={
              !!(emailFieldState.error?.message || emailFieldState.isTouched)
            }
            error={emailFieldState.error?.message}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              emailField.onChange(e.target.value);
              onEmailChange?.(e.target.value);
              onClearFormError?.("email");
            }}
            onBlur={emailField.onBlur}
          />
        </div>
        <div className="min-w-0 grow basis-58">
          <PhoneInput
            value={phoneNumberField.value}
            countryCode={countryCode}
            touched={phoneNumberFieldState.isTouched}
            error={phoneNumberFieldState.error?.message}
            onChange={(e) => {
              onMobilePhoneNumberChange?.(e.target.value);
              phoneNumberField.onChange(e.target.value);
            }}
            onBlur={phoneNumberField.onBlur}
          />
        </div>
      </div>
      <CheckBox
        onChange={() => {
          const next = !consentCheckboxField.value;
          onConsentChange?.(next);
          consentCheckboxField.onChange(next);
        }}
        checked={consentCheckboxField.value}
        error={consentCheckboxFieldState.error?.message}
        className="my-7"
        label={
          <Trans
            ns="loanWidget"
            i18nKey="loanWidget.privacyPolicyConsent"
            values={{ company: getCompanyBrandName(countryCode) }}
            components={{
              1: (
                <PrivacyPolicyAnchor
                  countryCode={countryCode}
                  countryWebsiteUrl={countryWebsiteUrl}
                  language={i18n.language as SupportedLanguage}
                />
              ),
            }}
          />
        }
        onBlur={consentCheckboxField.onBlur}
      />
      <Button
        size="lg"
        tabIndex={0}
        disabled={!formState.isValid}
        className={cn("text-center w-full bg-primary hover:bg-primary/90")}
        type="submit"
        variant="default"
      >
        {submitButtonText}
      </Button>
      <ul
        className="mt-4 flex list-none flex-wrap justify-center gap-2 p-0"
        aria-label={t("loanWidget.trustTagsListLabel")}
      >
        <li>
          <Badge variant="successLight">
            <Check className="size-4 mr-1" />
            <span>{t("loanWidget.trustTagFreeOfCharge")}</span>
          </Badge>
        </li>
        <li>
          <Badge variant="successLight">
            <Check className="size-4 mr-1" />
            <span>{t("loanWidget.trustTagNoCommitment")}</span>
          </Badge>
        </li>
        <li>
          <Badge variant="successLight">
            <Check className="size-4 mr-1" />
            <span>{t("loanWidget.trustTagImmediateResponse")}</span>
          </Badge>
        </li>
      </ul>
    </>
  );

  return (
    <div className="content-center">
      <span ref={stepAnchorRef} aria-hidden="true" className="block" />
      {isSubmitting && (
        <div className="mt-7 flex min-h-[500px] h-full w-full flex-col items-center justify-center gap-4">
          <LoadingSpinner />
          {/* typography: brand-colored loading text, preset would force black */}
          <p className="mt-4 text-center font-semibold text-base text-primary">
            {t("loanWidget.loadingText")}
          </p>
        </div>
      )}

      {!isSubmitting && (
        <form
          onSubmit={handleSubmit(onSubmit)}
          onFocusCapture={
            collapsible
              ? (event) => {
                  // The expand/collapse button manages formExpanded itself —
                  // auto-expanding on its focus would make its onClick toggle
                  // straight back on the first click.
                  if (
                    !formExpanded &&
                    !expandToggleRef.current?.contains(event.target)
                  ) {
                    setFormExpanded(true);
                  }
                }
              : undefined
          }
        >
          {formTitleText && (
            <h3 className="text-page-title">{formTitleText}</h3>
          )}

          {benefitTextToUse && (
            <div className="my-4 flex items-start gap-1.5 text-sm text-black">
              <Zap
                className="mt-0.5 size-4 shrink-0 text-primary"
                strokeWidth={2}
                aria-hidden
              />
              <span>{benefitTextToUse}</span>
            </div>
          )}

          {!collapsible ? (
            <>
              <div>{organisationInput}</div>
              {restFields}
            </>
          ) : (
            // pt-9 reserves a row for the absolutely-positioned toggle. The
            // ghost button is taller than the label row it used to share, so
            // without this its bottom edge sits flush on the first input.
            <div className="relative pt-9">
              <div
                className={cn(
                  "relative overflow-hidden transition-[max-height] duration-300 ease-out",
                  formExpanded ? "max-h-[4000px]" : "max-h-[min(150px,25vh)]",
                )}
              >
                {organisationInput}
                {restFields}
              </div>
              {!formExpanded && (
                <div
                  className="pointer-events-none absolute inset-x-0 bottom-0 z-5 h-14 bg-linear-to-t from-primary-150 to-transparent"
                  aria-hidden
                />
              )}
              <Button
                ref={expandToggleRef}
                type="button"
                variant="secondary"
                size="sm"
                aria-expanded={formExpanded}
                // bg-primary-150 not the variant's white: the widget surface is
                // primary-150, so a white button reads as a patch on it
                className="absolute right-0 top-0 z-10 bg-primary-150"
                onClick={() => setFormExpanded((open) => !open)}
              >
                <span className="select-none whitespace-nowrap">
                  {formExpanded
                    ? t("loanWidget.collapseCalculatorForm")
                    : t("loanWidget.expandCalculatorForm")}
                </span>
                {formExpanded ? (
                  <ChevronUp className="size-4" aria-hidden />
                ) : (
                  <ChevronDown className="size-4" aria-hidden />
                )}
              </Button>
            </div>
          )}
        </form>
      )}
    </div>
  );
};
